import type { ReactNode } from "react";
import { AlertTriangle, Pencil } from "lucide-react";
import { Modal } from "../Modal";
import StudentFinancialSummary from "../StudentFinancialSummary";
import { Pairs, RecordSheet, SectionBar } from "../RecordSections";
import type { Student } from "../../services/studentService";
import {
  STUDENT_STATUS_DOTS,
  STUDENT_STATUS_LABELS,
  formatSchoolDate,
  primaryGuardian,
  studentInitials,
} from "./studentDisplay";

type Props = {
  student: Student;
  canEdit: boolean;
  canViewFinancials: boolean;
  onEdit: () => void;
  onClose: () => void;
};

export default function StudentProfileModal({
  student,
  canEdit,
  canViewFinancials,
  onEdit,
  onClose,
}: Props) {
  const parents = student.parents_data || [];
  const primaryId = primaryGuardian(student)?.id;
  const healthAlerts = [
    ["Allergies", student.allergies],
    ["Medical conditions", student.medical_conditions],
  ].filter(([, value]) => value?.trim());

  return (
    <Modal
      isOpen
      size="4xl"
      title="Student record"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Close
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="btn btn-primary gap-2">
              <Pencil className="h-4 w-4" /> Edit record
            </button>
          )}
        </>
      }
    >
      <RecordSheet>
        <header className="flex flex-wrap items-center gap-3 px-4 py-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
            {studentInitials(student)}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold leading-6 text-gray-900">
              {student.full_name}
            </h2>
            <p className="font-mono text-xs text-gray-500">
              {student.student_id}
              {student.admission_number && ` · Adm. ${student.admission_number}`}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5 text-xs font-medium">
            <span className="inline-flex items-center gap-1.5 rounded border border-gray-200 px-2 py-0.5 text-gray-700">
              <span
                className={`h-1.5 w-1.5 rounded-full ${STUDENT_STATUS_DOTS[student.status] || "bg-gray-400"}`}
              />
              {STUDENT_STATUS_LABELS[student.status] || student.status}
            </span>
            <span className="rounded bg-primary-50 px-2 py-0.5 text-primary-700">
              {student.current_class_name || "No class"}
            </span>
            <span className="rounded bg-gray-100 px-2 py-0.5 text-gray-700">
              {student.residence === "boarding" ? "Boarder" : "Day scholar"}
            </span>
            {student.uses_transport && (
              <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-800">
                School transport
              </span>
            )}
          </div>
        </header>

        {healthAlerts.length > 0 && (
          <div
            role="note"
            className="flex gap-2 border-t border-amber-200 bg-amber-50 px-4 py-2 text-amber-900"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p>
              {healthAlerts.map(([label, value], index) => (
                <span key={label}>
                  {index > 0 && " · "}
                  <strong className="font-semibold">{label}:</strong> {value}
                </span>
              ))}
            </p>
          </div>
        )}

        <SectionBar letter="A" title="Learner details" />
        <Pairs
          rows={[
            [
              "Date of birth",
              student.date_of_birth &&
                `${formatSchoolDate(student.date_of_birth)} (${student.age} yrs)`,
            ],
            ["Gender", <span className="capitalize">{student.gender}</span>],
            ["Nationality", student.nationality],
            ["Blood group", student.blood_group],
          ]}
        />

        <SectionBar letter="B" title="Enrolment & transport" />
        <Pairs
          rows={[
            ["Class", student.current_class_name],
            ["Admitted", formatSchoolDate(student.admission_date)],
            ["Previous school", student.previous_school],
            [
              "SurePay no.",
              student.surepay_number && (
                <span className="font-mono">{student.surepay_number}</span>
              ),
            ],
            ["School transport", student.uses_transport ? "Yes" : "No"],
            ...(student.uses_transport
              ? ([
                  ["Route", student.transport_route],
                  ["Pickup point", student.pickup_location],
                ] as [string, ReactNode][])
              : []),
          ]}
        />

        <SectionBar letter="C" title="Parents & guardians" />
        <div className="overflow-x-auto px-4 py-3">
          <table className="w-full text-left">
            <thead className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="w-8 py-1 pr-3 font-semibold">No.</th>
                <th className="py-1 pr-3 font-semibold">Name</th>
                <th className="py-1 pr-3 font-semibold">Relationship</th>
                <th className="py-1 pr-3 font-semibold">Phone</th>
                <th className="py-1 font-semibold">Email</th>
              </tr>
            </thead>
            <tbody>
              {parents.map((parent, index) => {
                const phone = parent.user.phone_number || parent.emergency_contact;
                return (
                  <tr key={parent.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-1.5 pr-3 text-gray-500">{index + 1}.</td>
                    <td className="py-1.5 pr-3 font-medium text-gray-900">
                      {parent.full_name ||
                        `${parent.user.first_name} ${parent.user.last_name}`}
                      {parent.id === primaryId && (
                        <span className="ml-2 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700">
                          Primary
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 capitalize text-gray-700">
                      {parent.relationship || "—"}
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3">
                      {phone ? (
                        <a href={`tel:${phone}`} className="text-primary-700 hover:underline">
                          {phone}
                        </a>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-1.5">
                      {parent.user.email ? (
                        <a
                          href={`mailto:${parent.user.email}`}
                          className="break-all text-primary-700 hover:underline"
                        >
                          {parent.user.email}
                        </a>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!parents.length && (
                <tr>
                  <td colSpan={5} className="py-2 text-gray-500">
                    No parent or guardian is linked to this learner yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <SectionBar letter="D" title="Health" />
        <Pairs
          rows={[
            ["Conditions", student.medical_conditions],
            ["Allergies", student.allergies],
            ["Medications", student.medications],
            [
              "Doctor",
              [student.doctor_name, student.doctor_phone].filter(Boolean).join(" · "),
            ],
          ]}
        />

        <SectionBar letter="E" title="Portal login" />
        <Pairs
          rows={[
            ["Username", <span className="font-mono">{student.user.username}</span>],
            ["Email", student.user.email],
            ["Phone", student.user.phone_number],
          ]}
        />

        {canViewFinancials && (
          <>
            <SectionBar letter="F" title="Fees summary" />
            <div className="px-4 pb-3">
              <StudentFinancialSummary
                studentId={student.id}
                eventListClassName="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs"
              />
              <p className="mt-2 text-xs text-gray-500">
                Full statement, with CSV and PDF export: Financial → Statements.
              </p>
            </div>
          </>
        )}

        <div className="flex gap-4 border-t border-gray-200 bg-gray-50 px-4 py-2">
          <span className="whitespace-nowrap text-gray-500">Remarks:</span>
          <p className="whitespace-pre-line text-gray-900">
            {student.notes?.trim() || <span className="text-gray-400">None recorded.</span>}
          </p>
        </div>
      </RecordSheet>
    </Modal>
  );
}
